import React, { useState, useEffect, Fragment } from 'react';
import { 
  Card, CardContent, Typography, Box, Button, Dialog, DialogContent,
  FormControl, OutlinedInput, InputLabel, Select, MenuItem, TextField, Alert,
  Slide, Chip, Avatar, CircularProgress, alpha, useTheme
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from "react-query";
import { useNavigate, useParams } from 'react-router-dom';
import { Currency } from '@depay/local-currency';
import { QRCode } from 'react-qrcode-logo';
import { CopyToClipboard } from 'react-copy-to-clipboard';

// Icons
import { 
  SmartToy as BotIcon,
  Foundation as FoundationIcon,
  TrendingUp as ApexIcon,
  Diamond as SovereignIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

import apiClient from "../../request/http-common";
import { setInvestments } from '../../request/Analysis/hitorySlice';

// Transition Component
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CategoryPlans = () => {
  const theme = useTheme();
  
  const getCategoryConfig = (categoryName) => {
    const configs = {
      'tier capital structure': {
        title: 'Tier Capital Structure',
        subtitle: 'Institutional-grade investment tiers designed for capital stability and strategic growth',
        color: '#F7931A'
      },
      'trading bots': {
        title: 'AI Trading Bots',
        subtitle: 'Advanced algorithmic trading powered by machine learning',
        color: '#667eea'
      },
      'signals': {
        title: 'Trading Signals',
        subtitle: 'Professional market analysis and entry signals',
        color: '#f093fb'
      },
      'copy trading': {
        title: 'Copy Trading',
        subtitle: 'Mirror successful traders automatically',
        color: '#4facfe'
      }
    };
    return configs[categoryName?.toLowerCase()] || {
      title: categoryName,
      subtitle: 'Professional trading solutions',
      color: '#F7931A'
    };
  };

  const getPlanTier = (plan) => {
    // If plan has tier_type and related fields from database, use them
    if (plan.tier_type && ['foundation', 'apex', 'sovereign'].includes(plan.tier_type)) {
      const tierConfigs = {
        'foundation': {
          label: 'Foundation',
          color: '#F7931A',
          gradient: 'linear-gradient(135deg, #F7931A 0%, #E8851A 100%)',
          icon: <FoundationIcon />
        },
        'apex': {
          label: 'Apex',
          color: '#F7931A',
          gradient: 'linear-gradient(135deg, #E8851A 0%, #D97706 100%)',
          icon: <ApexIcon />
        },
        'sovereign': {
          label: 'Sovereign',
          color: '#F7931A',
          gradient: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
          icon: <SovereignIcon />
        }
      };
      
      const config = tierConfigs[plan.tier_type];
      const characteristics = plan.characteristics 
        ? (typeof plan.characteristics === 'string' ? JSON.parse(plan.characteristics) : plan.characteristics)
        : [];
      
      return {
        label: config.label,
        name: plan.name,
        subtitle: plan.subtitle || '',
        color: config.color,
        gradient: config.gradient,
        tier: plan.tier_type,
        icon: config.icon,
        minAmount: parseFloat(plan.min_amount),
        maxAmount: (plan.amount_invested && parseFloat(plan.amount_invested) < 999999999) ? parseFloat(plan.amount_invested) : null,
        purpose: plan.purpose || '',
        description: plan.description || '',
        characteristics: characteristics,
        minReturn: parseFloat(plan.initial_earning) || 0,
        maxReturn: parseFloat(plan.total_earning) || 0,
        tradeDuration: plan.trade_duration || ''
      };
    }
    
    // Fallback: determine tier by amount if no tier_type
    const amount = parseFloat(plan.min_amount);
    if (amount >= 10000) return { 
      label: 'Elite', 
      color: '#F7931A', 
      tier: 'premium',
      features: ['Priority Support', 'Advanced Analytics', 'Custom Strategies', 'Dedicated Manager']
    };
    if (amount >= 1000) return { 
      label: 'Pro', 
      color: '#10B981', 
      tier: 'pro',
      features: ['24/7 Trading', 'Risk Management', 'Market Analysis', 'Performance Reports']
    };
    return { 
      label: 'Starter', 
      color: '#6366F1', 
      tier: 'starter',
      features: ['Basic Trading', 'Standard Support', 'Weekly Reports', 'Email Notifications']
    };
  };

  const getPerformanceMetrics = (plan) => {
    const roi = parseFloat(plan.total_earning);
    return {
      roi: roi,
      riskLevel: roi > 50 ? 'High' : roi > 25 ? 'Medium' : 'Low',
      timeframe: plan.duration,
      successRate: Math.min(95, 65 + (roi * 0.5)), // Simulated success rate
      avgReturn: `${(roi / 12).toFixed(1)}%`, // Monthly average
    };
  };

  const coinRates = useSelector((state) => state.coins.coinRates);
  const fundings = useSelector((state) => state.wallet.fundings);
  const tradings = useSelector((state) => state.history.tradings);
  const addresses = useSelector((state) => state.coins.addresses);

  const params = useParams();
  
  const calculateBalance = (coin) => {
    let amount = 0;
    fundings.forEach(fund => {
      if (fund.gateway === coin) {
        amount += parseFloat(fund.amount);
      }
      });

      tradings.forEach(trade => {
        if (trade.pair === coin) {
          amount += parseFloat(trade.profit);
        }
        });
    
      return amount;
  }

  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = useState(0);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  
  const navigate = useNavigate();
  const plans = useSelector((state) => state.plans.plans);
  const categories = useSelector((state) => state.plans.categories);
  const user = useSelector((state) => state.user.user);

    const fundingBal = useSelector((state) => state.wallet.fundingBalance);
    const subBal = useSelector((state) => state.wallet.subscriptionBalance);
    const refBal = useSelector((state) => state.wallet.referralBalance);
    const mainBal = useSelector((state) => state.wallet.withdrawableBalance);
  
  const formatPrice = (amount)=> new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);

  const dispatch = useDispatch();
  const [rate, setRate] = useState(1);

  const callRate = async()=> {
    if(user.currency === 'USD'){
      setRate(1);
    }else{
      await Currency.fromUSD({ amount: 1 }).then(amount=>{
        setRate(amount.amount);
       });
    }  
};

    const setcategories = (number_of_downlines, total_earning, initial_earning) => {
        const categories = [];
        for (let i = 0; i <= parseInt(number_of_downlines); i++) {
            let heads = '';
            for (let j = 1; j <= i; j++) {
                heads += '🙂';
            }

            if (i === 0) {
              const initialPercentage = (parseInt(initial_earning) / parseInt(total_earning))*100;
                categories.push('INITIAL ROI = '+initialPercentage.toFixed(2)+'%');
            }else if (i === parseInt(number_of_downlines)) {
                categories.push('STAGE '+i +' = ' + heads+ '' );
            } else {
                categories.push('STAGE '+i +' = ' + heads);
            }
        }
        return categories;
    }

    const setAmounts = (initial_earning, total_earning, number_of_downlines, bonus_per_downline, amount_invested) => {
      const initialPercentage = (parseInt(initial_earning) / parseInt(total_earning))*100;
      const amounts = [initialPercentage.toFixed(2)];
      
      for (let downlines = 1; downlines <= parseInt(number_of_downlines); downlines++) {
        amounts.push(parseInt(bonus_per_downline * downlines))
      }

        return amounts;
    }

    const [planId, setPlanId] = useState();
    const [selectedPlan, setSelectedPlan] = useState('');
    const [source, setSource] = useState('');
    const [currentDuration, setCurrentDuration] = useState('');
    const [convertedAmount, setConvertedAmount] = useState(0);
    const [error, setErros] = useState('');

     // for new balance method
     const [buttonText, setbuttonText] = useState(null);
     const [selectedCoin, setSelectedCoin] = useState(null);
 
     const getCoinDetails = (coinName) => {
       switch (coinName) {
         case 'Overall Asset':
           setSelectedCoin(null) 
           break;
         case 'Referral Wallet':
           setSelectedCoin(null) 
           break;
         default:
           addresses.forEach(address => {
             if (address.coin_name === coinName) {       
               setSelectedCoin(address)            
             }
           });
           break;
       }
     }

    const { isLoading: isInvesting, mutate: postInvestment } = useMutation(
        async () => {
          return await apiClient.post(`/api/invest`, {
            investment_plan_id: planId,
            amount: amount, 
            rate: rate,
            duration: currentDuration,
            payment_source: source
          });
        },
        {
          onSuccess: (res) => {
              if (res.data.status === 'success') {
                localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
                const event = new Event('newMessage');
                window.dispatchEvent(event);
                dispatch(setInvestments(res.data.investments));
                setTimeout(() => {
                  navigate('/investments');
                }, 2000);
              }            
          },
          onError: (err) => {
                if (err.response?.data.length) {
                  let myerror = err.response?.data || err;         
              setErros(myerror.errors)               
              }else{
                  let errorMessage = err.response?.data.message || err ;
                  localStorage.setItem('notification', JSON.stringify({message : `${errorMessage}`, 'type': 'error' }));
                  const event = new Event('newMessage');
                  window.dispatchEvent(event);
              }
          },
        }
      );

      
      useEffect(() => {
        callRate();
      }, []);

  const categoryConfig = getCategoryConfig(params.id);
  const categoryPlans = plans.filter(plan => plan.category.name === params.id);
  const isTierCapitalStructure = params.id?.toLowerCase() === 'tier capital structure';

  return (
    <Box sx={{ 
      paddingX: 2,
      paddingTop: { xs: 2, sm: 1, md: 0 },
      paddingBottom: 3,
      maxWidth: isTierCapitalStructure ? '1200px' : '800px',
      margin: '0 auto'
    }}>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
        <Typography 
          variant="h4"
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
            color: theme.palette.text.primary,
            marginBottom: 1.5,
            letterSpacing: '-0.02em'
          }}
        >
          {categoryConfig.title || params.id}
        </Typography>
        <Typography 
          variant="body1"
          sx={{ 
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: 1.6
          }}
        >
          {categories.find(cat => cat.name === params.id)?.description || categoryConfig.subtitle}
        </Typography>
      </Box>

        {/* Plans List */}
        {categoryPlans.length > 0 && (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: isTierCapitalStructure 
              ? { xs: '1fr', md: 'repeat(3, 1fr)' } 
              : { xs: '1fr' },
            gap: 3
          }}>
            {categoryPlans.map((plan, index) => {
              const tierInfo = getPlanTier(plan);
              const metrics = getPerformanceMetrics(plan);
              const isTierPlan = ['foundation', 'apex', 'sovereign'].includes(tierInfo.tier);
              
              if (isTierCapitalStructure && isTierPlan) {
                // New Tier Capital Structure Design
                return (
                  <Card 
                    key={plan.id}
                    sx={{ 
                      background: theme.palette.background.paper,
                      border: `2px solid ${tierInfo.color}`,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 40px ${alpha(tierInfo.color, 0.3)}`,
                        borderColor: tierInfo.color
                      }
                    }}
                  >
                    {/* Tier Badge */}
                    <Box sx={{
                      background: tierInfo.gradient,
                      padding: 1.5,
                      textAlign: 'center',
                      position: 'relative'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, marginBottom: 0.5 }}>
                        {tierInfo.icon && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            {React.cloneElement(tierInfo.icon, { sx: { fontSize: 24, color: 'white' } })}
                          </Box>
                        )}
                      </Box>
                      <Typography 
                        variant="h5"
                        sx={{ 
                          fontWeight: 800,
                          color: 'white',
                          fontSize: '1.5rem',
                          marginBottom: 0.5
                        }}
                      >
                        {tierInfo.name}
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}
                      >
                        {tierInfo.subtitle}
                      </Typography>
                    </Box>

                    <CardContent sx={{ padding: 3 }}>
                      {/* Description */}
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          lineHeight: 1.7,
                          marginBottom: 3,
                          minHeight: '60px'
                        }}
                      >
                        {tierInfo.description}
                      </Typography>

                      {/* Investment Range */}
                      <Box sx={{ 
                        background: alpha(tierInfo.color, 0.1),
                        borderRadius: '8px',
                        padding: 2,
                        marginBottom: 3,
                        border: `1px solid ${alpha(tierInfo.color, 0.2)}`
                      }}>
                        <Typography 
                          variant="caption"
                          sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: 0.5,
                            display: 'block'
                          }}
                        >
                          Investment Range
                        </Typography>
                        <Typography 
                          variant="h6"
                          sx={{ 
                            fontWeight: 700,
                            color: tierInfo.color,
                            fontSize: '1.1rem'
                          }}
                        >
                          {formatPrice(tierInfo.minAmount)}
                          {tierInfo.maxAmount === null || tierInfo.maxAmount === undefined || tierInfo.maxAmount >= 999999999 
                            ? ' - UNLIMITED' 
                            : ` - ${formatPrice(tierInfo.maxAmount)}`}
                        </Typography>
                      </Box>

                      {/* Purpose */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography 
                          variant="caption"
                          sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: 1,
                            display: 'block'
                          }}
                        >
                          Purpose
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: theme.palette.text.primary,
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          {tierInfo.purpose}
                        </Typography>
                      </Box>

                      {/* Characteristics */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography 
                          variant="caption"
                          sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: 1.5,
                            display: 'block'
                          }}
                        >
                          Characteristics
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {tierInfo.characteristics?.map((char, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <CheckIcon sx={{ 
                                fontSize: 18, 
                                color: tierInfo.color, 
                                marginTop: '2px',
                                flexShrink: 0
                              }} />
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  color: theme.palette.text.primary,
                                  fontSize: '0.875rem',
                                  lineHeight: 1.5
                                }}
                              >
                                {char}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Trade Duration */}
                      {tierInfo.tradeDuration && (
                        <Box sx={{ marginBottom: 3 }}>
                          <Typography 
                            variant="caption"
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: 1,
                              display: 'block'
                            }}
                          >
                            Typical trade/investment duration
                          </Typography>
                          <Typography 
                            variant="body2"
                            sx={{ 
                              color: theme.palette.text.primary,
                              fontSize: '0.875rem',
                              lineHeight: 1.7,
                              whiteSpace: 'pre-line'
                            }}
                          >
                            {tierInfo.tradeDuration}
                          </Typography>
                        </Box>
                      )}

                      {/* ROI Display */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: 1.5,
                        background: alpha('#10B981', 0.1),
                        borderRadius: '8px',
                        marginBottom: 2,
                        border: `1px solid ${alpha('#10B981', 0.2)}`
                      }}>
                        <Box>
                          <Typography 
                            variant="caption"
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                              display: 'block',
                              marginBottom: 0.5
                            }}
                          >
                            Target annualized return
                          </Typography>
                          <Typography 
                            variant="h6"
                            sx={{ 
                              fontWeight: 800,
                              color: '#10B981',
                              fontSize: '1.25rem'
                            }}
                          >
                            {tierInfo.minReturn > 0 && tierInfo.maxReturn > tierInfo.minReturn
                              ? `${tierInfo.minReturn.toFixed(0)}% - ${tierInfo.maxReturn.toFixed(0)}%${tierInfo.tier === 'sovereign' ? ' (not every year)' : ''}`
                              : `${tierInfo.maxReturn.toFixed(0)}%`}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography 
                            variant="caption"
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                              display: 'block',
                              marginBottom: 0.5
                            }}
                          >
                            Risk Level
                          </Typography>
                          <Chip 
                            label={metrics.riskLevel}
                            size="small"
                            sx={{ 
                              background: metrics.riskLevel === 'Low' ? '#10B981' : 
                                        metrics.riskLevel === 'Medium' ? '#F7931A' : '#F84960',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Button
                        onClick={() => {
                          handleClickOpen();
                          setPlanId(plan.id);
                          setSelectedPlan(plan);
                          setConvertedAmount(parseFloat(plan.amount_invested));
                        }}
                        disabled={isInvesting && planId === plan.id}
                        fullWidth
                        sx={{
                          background: tierInfo.gradient,
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          padding: '12px 24px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: tierInfo.gradient,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 20px ${alpha(tierInfo.color, 0.4)}`
                          },
                          '&:disabled': {
                            background: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled
                          }
                        }}
                      >
                        {isInvesting && planId === plan.id ? (
                          <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                          'Start Investment'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              } else {
                // Original design for other plans
                return (
                  <Card 
                    key={plan.id}
                    sx={{ 
                      background: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 8px 24px rgba(0, 0, 0, 0.4)' 
                          : '0 8px 24px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <CardContent sx={{ padding: '16px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32,
                            background: `linear-gradient(135deg, ${tierInfo.color}, ${alpha(tierInfo.color, 0.8)})`,
                          }}
                        >
                          <BotIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Avatar>
                        
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 0.5 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: theme.palette.text.primary
                              }}
                            >
                              {plan.name}
                            </Typography>
                            {tierInfo.tier === 'premium' && (
                              <Chip 
                                label="0 Fee Promo"
                                size="small"
                                sx={{ 
                                  background: '#FF6B35',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: 18,
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 0.5 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 800,
                                fontSize: '1.1rem',
                                color: '#10B981'
                              }}
                            >
                              {metrics.roi.toFixed(2)}%
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: '0.75rem'
                              }}
                            >
                              Highest WPR
                            </Typography>
                          </Box>
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '0.8rem',
                              lineHeight: 1.3,
                              marginBottom: 1
                            }}
                          >
                            Auto invest regardless of market conditions | For {metrics.riskLevel} Risk
                          </Typography>
                        </Box>

                        <Button
                          onClick={() => {
                            handleClickOpen();
                            setPlanId(plan.id);
                            setSelectedPlan(plan);
                            setConvertedAmount(parseFloat(plan.amount_invested));
                          }}
                          disabled={isInvesting && planId === plan.id}
                          sx={{
                            background: '#F7931A',
                            color: 'white',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            padding: '6px 16px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: 'auto',
                            '&:hover': {
                              background: '#E8851A'
                            },
                            '&:disabled': {
                              background: theme.palette.action.disabledBackground,
                              color: theme.palette.action.disabled
                            }
                          }}
                        >
                          {isInvesting && planId === plan.id ? (
                            <CircularProgress size={16} sx={{ color: 'white' }} />
                          ) : (
                            'Create Now'
                          )}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              }
            })}
          </Box>
        )}

        {/* Minimal Investment Modal */}
      <Dialog
          fullWidth
          maxWidth="sm"
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              margin: 2
            }
          }}
        >
          <DialogContent sx={{ padding: 3 }}>
            
            {/* Simple Header */}
            <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 1, color: theme.palette.text.primary }}>
                {selectedPlan ? `Start ${selectedPlan.name}` : 'Start Investment'}
              </Typography>
              {selectedPlan && (
                               <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                 {formatPrice(selectedPlan.min_amount)} - {parseInt(selectedPlan.amount_invested) >= 1000000 ? 'UNLIMITED' : formatPrice(selectedPlan.amount_invested)}
               </Typography>
              )}
            </Box>

            {/* Compact Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              
              {/* Amount Input */}
              <FormControl fullWidth>
                <InputLabel>Investment Amount</InputLabel>
              <OutlinedInput
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  type="number"
                  label="Investment Amount"
                  startAdornment={<Typography sx={{ marginRight: 1, color: theme.palette.text.secondary }}>$</Typography>}
              />
            </FormControl>

              {/* Duration Selection */}
              {selectedPlan !== '' && selectedPlan.duration && selectedPlan.duration.split(',').length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                        <Select
                    value={currentDuration}
                        onChange={(e) => setCurrentDuration(e.target.value)}
                    label="Duration"
                    error={error !== '' && error.duration}
                  >
                    {selectedPlan.duration && selectedPlan.duration.split(',').map((duration, index) => (
                      <MenuItem key={index} value={duration}>
                        {duration}
                            </MenuItem>              
                            ))}  
                        </Select>
                  {error !== '' && error.duration && (
                    <Typography color="error" variant="caption">
                      {error.duration}
                        </Typography>
                  )}
                    </FormControl>
              )}

              {/* Payment Source */}
              <FormControl fullWidth>
                <InputLabel>Payment Source</InputLabel>
                          <Select
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                    getCoinDetails(e.target.value);
                  }}
                  label="Payment Source"
                  error={error !== '' && error.source}
                >
                  <MenuItem value="Overall Asset">
                    Overall Asset (Balance: {formatPrice(fundingBal)})
                              </MenuItem> 
                          </Select>
                {error !== '' && error.source && (
                  <Typography color="error" variant="caption">
                    {error.source}
                          </Typography>
                )}
                      </FormControl>

              {/* Payment Details */}
              {selectedCoin !== null && (
                <Box>
                  <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {selectedCoin.wallet_type === 'bank' 
                      ? `Make payment to our ${selectedCoin.coin_name} account`
                      : `Send ${formatPrice(amount)} worth of ${selectedCoin.coin_name} to address below`
                    }
                        </Alert>                       
                      
                  {selectedCoin.wallet_type === 'Crypto' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                      <QRCode 
                        qrStyle="dots" 
                        value={selectedCoin.address} 
                        size={150}
                        logoImage=""
                      />
                      </Box>
                  )}

                  <CopyToClipboard 
                    onCopy={() => {
                      setbuttonText(selectedCoin.wallet_type === 'bank' ? 'Copied!' : 'Address Copied!');
                      setTimeout(() => setbuttonText(null), 2000);
                    }} 
                    text={selectedCoin.address}
                  >
                                  <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={selectedCoin.address}
                      label={buttonText || 'Click to copy address'}
                      sx={{ cursor: 'pointer' }}
                    />
                          </CopyToClipboard>
                          </Box>
              )}

              {/* Submit Button */}
              <Button 
                onClick={postInvestment} 
                disabled={isInvesting || !amount || !currentDuration || !source}
                variant="contained"
                size="large"
                sx={{
                  height: 48,
                  background: '#F7931A',
                  marginTop: 2,
                  '&:hover': {
                    background: '#E8851A'
                  }
                }}
                startIcon={isInvesting ? 
                  <CircularProgress size={20} sx={{ color: 'white' }} /> : 
                  null
                }
              >
                {isInvesting ? 'Processing...' : 'Start Investment'}
              </Button>
          </Box> 
        </DialogContent>
      </Dialog>
      </Box>
  )
}

export default CategoryPlans
